import { ApiResponse } from '../types/api';
import { getApiBaseUrl, resolveBackendType } from '../config/backend';

const BACKEND_TYPE = resolveBackendType(import.meta.env.VITE_BACKEND_TYPE);
const API_BASE_URL = getApiBaseUrl();

const AUTH_TOKEN_KEY = 'nad_auth_token';

function getStoredToken(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export class ApiClient {
  static async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    // GAS Web Apps use ?route= query param for routing; Laravel uses path-based routing.
    // In GAS mode the route path is separated from any query string so filters
    // (e.g. `/vendors?status=active`) are forwarded as real GAS request parameters.
    // GAS also cannot read HTTP headers, so the session token is sent as ?token=.
    const token = getStoredToken();
    let url: string;
    if (BACKEND_TYPE === 'GAS') {
      const clean = endpoint.replace(/^\//, '');
      const queryIndex = clean.indexOf('?');
      const path = queryIndex === -1 ? clean : clean.slice(0, queryIndex);
      const query = queryIndex === -1 ? '' : clean.slice(queryIndex + 1);
      const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
      url = `${API_BASE_URL}?route=${encodeURIComponent(path)}${query ? `&${query}` : ''}${tokenParam}`;
    } else {
      url = `${API_BASE_URL}${endpoint}`;
    }

    try {
      // GAS Web Apps issue 302 redirects, so we use redirect: 'follow'.
      // In GAS mode the token is sent via ?token= query param; we must NOT
      // set the Authorization header because it triggers a CORS preflight
      // (OPTIONS) that GAS does not handle — resulting in "Failed to fetch".
      const isGas = BACKEND_TYPE === 'GAS';
      const response = await fetch(url, {
        ...options,
        redirect: 'follow',
        headers: {
          ...(isGas ? {} : { 'Content-Type': 'application/json' }),
          ...(!isGas && token ? { Authorization: `Bearer ${token}` } : {}),
          ...(options.headers || {}),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
      }

      const text = await response.text();
      let json: ApiResponse<T>;
      try {
        json = JSON.parse(text);
      } catch (err) {
        json = {
          success: true,
          data: text as unknown as T,
          message: 'Received raw text response',
        };
      }

      return json;
    } catch (error: any) {
      return {
        success: false,
        data: null as unknown as T,
        message: error.message || 'Network communication failed',
        errors: [error.toString()],
      };
    }
  }

  static get<T = any>(endpoint: string, options: RequestInit = {}) {
    return this.request<T>(endpoint, { method: 'GET', ...options });
  }

  static post<T = any>(endpoint: string, body: any) {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static put<T = any>(endpoint: string, body: any) {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static patch<T = any>(endpoint: string, body: any) {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static delete<T = any>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}
